import { isPhoneAndWhatsappValid } from '../input-phone';

const BASE_FIELDS_ONSHORE = ['fullname', 'email', 'phone', 'nationality', 'city'];
const BASE_FIELDS_OFFSHORE = ['fullname', 'email', 'phone', 'nationality'];
const SYSTEM_FIELDS = ['visa_type', 'visa_month', 'visa_year', 'school', 'other_school', 'inter-phone'];

class LandingAutosave {
    constructor(options = {}) {
        Object.assign(this, options);
        this.autosaveTriggered = false;
        this.inscriptionGuid = null;
        this.autosaveInProgress = false;
        this.saveProgressDebounceTimer = null;
        this._activeSavePromise = null;

        if (!this.landingId || !this.lang || !this.csrfToken) {
            console.error('LandingAutosave: landingId, lang, and csrfToken are required');
            return;
        }

        window.__landingAutosave = this;
        this.setupEventListeners();
    }

    syncPhoneHiddenBeforeCollect() {
        const phoneInput = document.querySelector('#phone');
        const interPhone = document.querySelector('#inter-phone');
        if (!phoneInput || !interPhone || !phoneInput.value.trim()) {
            return;
        }
        if (typeof window.intlTelInput === 'function' && typeof window.intlTelInput.getInstance === 'function') {
            const instance = window.intlTelInput.getInstance(phoneInput);
            if (instance && typeof instance.getNumber === 'function') {
                const fullNumber = instance.getNumber();
                if (fullNumber) {
                    interPhone.value = String(fullNumber).replace(/\+0+/, '+');
                }
            }
        }
    }

    // City is only asked of onshore students, and a student-type question can add or remove it
    // after the page loaded, so the form itself says whether it is part of the base fields.
    baseFields() {
        return $('#city').length ? BASE_FIELDS_ONSHORE : BASE_FIELDS_OFFSHORE;
    }

    setupEventListeners() {
        // Base fields selector including destination (use event delegation for dynamically loaded fields)
        const baseFieldsSelector = [...BASE_FIELDS_ONSHORE, 'destination'].map(f => `#${f}`).join(', ');

        // Autosave and error clearing for base fields (including destination)
        $(document).on({
            'blur.autosave change.autosave': () => this.tryAutoSave(),
            'input.autosave': (e) => this.clearFieldError(e.target.id)
        }, baseFieldsSelector);

        // Update submit button state on input change
        $(document).on('input change', baseFieldsSelector + ', #questions-content :input', () => {
            this.updateSubmitButtonState()
        });

        // Autosave for Marketing questions
        $(document).on('blur.autosave change.autosave', '#questions-content :input',
            () => {
                if (this.autosaveTriggered && this.allInitialAnswered()) {
                    this.saveProgress();
                }
            }
        );

        // Listen for phone validation completion and trigger autosave
        $(document).on('phoneValidationComplete', '#inter-phone, #phone', () => {
            this.tryAutoSave();
        });
    }

    collectInitialData() {
        this.resetHiddenFields();

        const data = {
            landing_page_id: this.landingId,
            language: this.lang,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...this.collectSystemFields(),
            ...this.collectUtmFields(),
        };

        const fields = {
            full_name: '#fullname',
            email: '#email',
            phone: '#inter-phone',
            nationality: '#nationality',
        };

        // Add destination if it exists
        if ($('#destination').length) {
            fields.destination = '#destination';
        }

        Object.entries(fields).forEach(([key, selector]) => {
            const val = $(selector).val();
            if (val) {
                data[key] = val;
            }
        });
        
        // Marketing questions
        const marketingQuestions = this.collectMarketingQuestions();
        if (Object.keys(marketingQuestions).length) {
            data.marketing_questions = marketingQuestions;
        }

        // Inscription
        if (this.inscriptionGuid) {
            data.inscription_guid = this.inscriptionGuid;
        }

        // Onshore student → city
        const city = $('#city').val();
        if (city) data.city_id = city;

        // Send how_continue if it has a value. In wizard mode it's a hidden input behind
        // button options, so :visible alone can't tell us if it's been answered.
        const howContinueEl = $('#how_continue');
        const howContinue = howContinueEl.val();
        const howContinueUsable = howContinueEl.is(':visible') || howContinueEl.hasClass('landing-embed-option-value');
        if (howContinueEl.length && howContinueUsable && typeof howContinue !== 'undefined' && howContinue !== null && howContinue !== '') {
            data.how_continue = howContinue;
        }

        return data;
    }

    collectSystemFields() {
        const systemData = {};
        SYSTEM_FIELDS.forEach(fieldId => {
            const el = $(`#${fieldId}`);
            const fieldValue = el.val();
            if (el.length && el.is(':visible') && fieldValue) {
                systemData[fieldId] = fieldValue;
            }
        });

        return systemData;
    }

    collectMarketingQuestions() {
        const answers = {};
        const baseFields = [...BASE_FIELDS_OFFSHORE, 'how_continue', 'city', 'destination'];
        const systemFields = [...SYSTEM_FIELDS, 'city_id'];
        const excludeFields = [...baseFields, ...systemFields];
        const hasWizardInner = $('#questions-content .landing-wizard-inner').length > 0;

        $('#questions-content').find('input, select, textarea').each(function () {
            const $el = $(this);
            if ($el.closest('.calendar-field').length || $el.closest('#calendar-wrapper').length) {
                return;
            }
            const id = $el.attr('id');
            const isWizardOptionHidden = $el.hasClass('landing-embed-option-value');
            const isNumberWheelInput = $el.hasClass('landing-embed-number-wheel-input');
            const isSelect2 = $el.hasClass('select2-hidden-accessible');
            const inWizardInner = $el.closest('.landing-wizard-inner').length > 0;
            const skipBecauseHidden =
                $el.is(':hidden')
                && !isWizardOptionHidden
                && !isNumberWheelInput
                && !isSelect2
                && !(hasWizardInner && inWizardInner);
            if (!id || excludeFields.includes(id) || skipBecauseHidden) {
                return;
            }

            if ($el.is(':checkbox')) {
                if ($el.is(':checked')) {
                    answers[id] = true;
                }
            } else if ($el.is(':radio')) {
                if ($el.is(':checked') && $el.val()) {
                    answers[id] = $el.val();
                }
            } else {
                const val = $el.val();
                if (val) {
                    answers[id] = val;
                }
            }
        });

        return answers;
    }

    collectUtmFields() {
        const utmFields = {};
        const allowedNames = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

        allowedNames.forEach(name => {
            const el = $(`[name="${name}"]`);
            if (el.length) {
                const value = el.val();
                if (value) {
                    utmFields[name] = value;
                }
            }
        });

        return utmFields;
    }

    allInitialAnswered() {
        const finalRequiredFields = [...this.baseFields()];
        if ($('#destination').length) {
            finalRequiredFields.push('destination');
        }
        const requiredSelectors = finalRequiredFields.map(f => `#${f}`);
        const allFieldsFilled = requiredSelectors.every(selector => $(selector).val());
        const emailValid = this.isEmailValid();
        const phoneInput = document.querySelector('#phone');

        if (!phoneInput || !phoneInput.value.trim()) {
            return allFieldsFilled && emailValid;
        }
        
        if (!isPhoneAndWhatsappValid(phoneInput)) {
            return false;
        }
        
        return allFieldsFilled && emailValid;
    }

    isEmailValid() {
        const emailInput = document.querySelector('#email');
        
        if (!emailInput) {
            return true;
        }

        const emailValue = emailInput.value;
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        
        return emailRegex.test(emailValue);
    }

    tryAutoSave() {
        if (!this.autosaveInProgress && this.allInitialAnswered()) {
            if (!this.autosaveTriggered) {
                this.saveProgress(true);
            } else {
                this.saveProgress(false);
            }
        }
    }

    updateSubmitButtonState() {
        if ($('#form-calendar').length && $('#questions-content .landing-wizard-inner').length) {
            return;
        }

        const hasCalendar = $(document).data('hasCalendar');
        const allAnswered = this.allInitialAnswered();
        const howContinueVal = $('#how_continue').val();

        const isFilled = (element) => {
            if (element.length === 0) {
                return true;
            }

            if (element.hasClass('landing-embed-option-value')) {
                return !!element.val();
            }

            if (element.is(':hidden')) {
                return true;
            }

            if (element.is(':checkbox') || element.is(':radio')) {
                return element.is(':checked');
            }

            return !!element.val();
        };

        const systemFieldIds = Object.keys(this.collectSystemFields());
        const allSystemFieldsFilled = systemFieldIds.length === 0 || systemFieldIds.every((id) => isFilled($(`#${id}`)));

        const marketingEls = $('#questions-content :input').filter('[id]').filter(function () {
            const $el = $(this);
            if ($el.hasClass('landing-embed-option-value')) {
                return true;
            }
            return !$el.is(':hidden');
        });
        const allMarketingQuestionsFilled = marketingEls.length === 0 || marketingEls.toArray().every((el) => isFilled($(el)));

        // If whatsapp is selected, always show submit button
        if (howContinueVal === 'whatsapp') {
            $('#btn-submit-wrapper').removeClass('hidden');
            $('#btn-register').prop('disabled', false);
            return;
        }

        // Treat unset hasCalendar like "no calendar"
        const canSubmit = hasCalendar !== true && allAnswered && allSystemFieldsFilled && allMarketingQuestionsFilled;

        $('#btn-submit-wrapper').toggleClass('hidden', !canSubmit);
        $('#btn-register').prop('disabled', !canSubmit);
    }

    resetHiddenFields() {
        const wizardInnerPresent = $('#questions-content .landing-wizard-inner').length > 0;
        const preservedIds = new Set([...this.baseFields(), 'destination', 'how_continue', 'inter-phone']);

        // Hidden how_continue still holds the modality slug for the calendar API.
        // Reset extra questions if hidden
        $('#questions-content').find('input, select, textarea').each(function () {
            const $el = $(this);
            if ($el.closest('.calendar-field').length || $el.closest('#calendar-wrapper').length) {
                return;
            }
            const id = $el.attr('id');
            if ($el.hasClass('landing-embed-option-value')) {
                if ($el.closest('.field').hasClass('hidden')) {
                    // Wizard: do not clear hidden step values before collect.
                    if (!$el.closest('.landing-wizard-inner').length) {
                        $el.val('');
                    }
                }
                return;
            }
            if ($el.is(':hidden')) {
                if (wizardInnerPresent && $el.closest('.landing-wizard-inner').length) {
                    return;
                }
                if ($el.closest('.extra-question:not(.hidden)').length) {
                    return;
                }
                if (id && preservedIds.has(id)) {
                    return;
                }
                // Special handling for inter-phone
                if ($el.attr('id') === 'inter-phone') {
                    if (!$('#phone').val()) {
                        $el.val('');
                    }
                } else if ($el.is(':checkbox') || $el.is(':radio')) {
                    $el.prop('checked', false);
                } else {
                    $el.val('');
                }
            }
        });
    }

    saveProgress(isFirst = false) {
        if (isFirst) {
            if (this.saveProgressDebounceTimer) {
                clearTimeout(this.saveProgressDebounceTimer);
                this.saveProgressDebounceTimer = null;
            }
            this.executeSaveProgress(true);
            return;
        }
        if (this.saveProgressDebounceTimer) {
            clearTimeout(this.saveProgressDebounceTimer);
        }
        this.saveProgressDebounceTimer = setTimeout(() => {
            this.saveProgressDebounceTimer = null;
            this.executeSaveProgress(false);
        }, 320);
    }

    flushSaveForWizardAdvance() {
        this.syncPhoneHiddenBeforeCollect();
        if (this.saveProgressDebounceTimer) {
            clearTimeout(this.saveProgressDebounceTimer);
            this.saveProgressDebounceTimer = null;
        }
        const isFirst = !this.autosaveTriggered;
        return this.executeSaveProgress(isFirst);
    }

    executeSaveProgress(isFirst = false) {
        if (!this.allInitialAnswered()) {
            return Promise.resolve(null);
        }

        if (this.autosaveInProgress && this._activeSavePromise) {
            return this._activeSavePromise;
        }

        this.autosaveInProgress = true;
        this._activeSavePromise = new Promise((resolve, reject) => {
            $.ajax({
                url: `/${this.lang}/landing-auto-save`,
                type: 'POST',
                data: {
                    ...this.collectInitialData(),
                    _token: this.csrfToken
                },
                success: (res) => {
                    this.clearValidationErrors();

                    if (res.inscription_guid) {
                        this.inscriptionGuid = res.inscription_guid;

                        $(document).trigger('inscriptionGuidUpdated', [res.inscription_guid]);

                        if (isFirst) {
                            this.autosaveTriggered = true;

                            $(document).data('autosaveTriggered', true);
                            $(document).data('hasCalendar', res.has_calendar);
                            $(document).data('requireExtraQuestions', res.require_extra_questions);
                            $(document).data('inscriptionGuid', res.inscription_guid);
                            $(document).data('isQualified', res.is_qualified);

                            $(document).trigger('autosaveTriggered');

                            this.updateSubmitButtonState();
                        } else {
                            const previousHasCalendar = $(document).data('hasCalendar');
                            const previousRequireExtraQuestions = $(document).data('requireExtraQuestions');
                            const previousIsQualified = $(document).data('isQualified');
                            const previousInscriptionGuid = $(document).data('inscriptionGuid');
                            if (
                                previousHasCalendar !== res.has_calendar
                                || previousRequireExtraQuestions !== res.require_extra_questions
                                || previousIsQualified != res.is_qualified
                                || previousInscriptionGuid !== res.inscription_guid
                            ) {
                                $(document).data('hasCalendar', res.has_calendar);
                                $(document).data('requireExtraQuestions', res.require_extra_questions);
                                $(document).trigger('hasCalendarChanged', [res.has_calendar]);
                                $(document).data('isQualified', res.is_qualified);
                                $(document).data('inscriptionGuid', res.inscription_guid);

                                this.updateSubmitButtonState();
                            }

                            this.updateSubmitButtonState();
                        }
                    }

                    resolve(res);
                },
                error: (xhr, status, error) => {
                    console.error('Autosave failed:', error);

                    if (xhr.status === 422 && xhr.responseJSON && xhr.responseJSON.errors) {
                        this.displayValidationErrors(xhr.responseJSON.errors);
                    }

                    reject(xhr);
                },
                complete: () => {
                    this.autosaveInProgress = false;
                    this._activeSavePromise = null;
                },
            });
        });

        return this._activeSavePromise;
    }

    displayValidationErrors(errors) {
        this.clearValidationErrors();
        
        Object.entries(errors).forEach(([fieldName, messages]) => {
            if (messages.length > 0) {
                this.showFieldError(fieldName, messages[0]);
            }
        });
    }

    showFieldError(fieldName, message) {
        const fieldId = fieldName === 'full_name' ? 'fullname' : fieldName === 'city_id' ? 'city' : fieldName;
        const errorElement = $(`#error_${fieldId}`);
        const fieldElement = $(`#${fieldId}`);
        
        if (errorElement.length && fieldElement.length) {
            errorElement.html(message).addClass('show');
            fieldElement.addClass('is-danger');
            
            if (fieldElement.hasClass('select2-hidden-accessible')) {
                fieldElement.next('.select2-container').addClass('is-danger');
            }
        }
    }

    clearValidationErrors() {
        $('.help.is-danger').removeClass('show').empty();
        $('.input, .select select').removeClass('is-danger valid error');
        $('.select2-container').removeClass('is-danger');
    }

    clearFieldError(fieldId) {
        $(`#error_${fieldId}`).removeClass('show').empty();
        $(`#${fieldId}`).removeClass('is-danger valid error').next('.select2-container').removeClass('is-danger');
    }
}

// Make it available globally
window.LandingAutosave = LandingAutosave;

export async function awaitLandingAutosaveForWizardAdvance() {
    const autosave = window.__landingAutosave;
    if (!autosave || typeof autosave.flushSaveForWizardAdvance !== 'function') {
        return true;
    }

    try {
        await autosave.flushSaveForWizardAdvance();
        return true;
    } catch (e) {
        return false;
    }
}

export function updateStatusAfterSubmit(payload) {
    console.log("Updating lead status after submit...");
    $.ajax({
        url: "/landing/update-lead-status",
        method: "POST",
        data: payload
    }).done(() => {
        console.log("Lead status updated successfully.");
    }).fail((error) => {
        console.error("Failed to update lead status:", error);
    });
}
